import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  ButtonBase,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { Section } from "../commercial-dashboard/DashboardBits";
import {
  CHANNELS,
  MONTHS,
  bridge,
  byPerson,
  eur,
  eurSigned,
  groupBy,
  inPeriod,
  keur,
  pct,
  pctSigned,
  pp,
  sum,
  toneOfAmount,
  toneOfCompliance,
  toneOfMarginDiff,
} from "./controlBoardModel";
import {
  ComplianceBar,
  Note,
  Num,
  PeriodSelector,
  ShareRing,
  Signal,
  TypeLegend,
  useToneColor,
} from "./ControlBoardBits";
import { TabProps, companyIdsOf } from "./boardData";

interface Props extends TabProps {
  selected: string;
  onSelect: (account: string) => void;
}

/** Accounts: cuánto de su Business Plan consiguen y cómo reparten la inversión de su cartera. */
const AccountsTab: React.FC<Props> = ({
  data,
  selected,
  onSelect,
  onOpenClient,
}) => {
  const theme = useTheme();
  const toneColor = useToneColor();
  const detailRef = useRef<HTMLDivElement>(null);
  const [month, setMonth] = useState(0);
  const period = month
    ? `en ${MONTHS[month].toLowerCase()}`
    : `de enero a ${MONTHS[data.lastMonth].toLowerCase()}`;

  const accounts = useMemo(() => {
    const rows = inPeriod(data.rows, month, data.lastMonth);
    return [...byPerson(rows, (r) => r.accounts).entries()]
      .map(([name, list]) => {
        const plan = inPeriod(
          data.planFor(companyIdsOf(list)),
          month,
          data.lastMonth
        );
        const b = bridge(list, plan);
        const outOfPlan = sum(
          CHANNELS.filter((c) => !b.channels[c].planInvestment),
          (c) => b.channels[c].real.sale
        );
        return {
          name,
          list,
          b,
          clients: new Set(list.map((r) => r.clientId)).size,
          compliance: b.planInvestment
            ? b.realInvestment / b.planInvestment
            : NaN,
          profitCompliance: b.planProfit ? b.realProfit / b.planProfit : NaN,
          shift: b.realShareCM - b.planShareCM,
          outOfPlan: b.planInvestment ? outOfPlan : 0,
        };
      })
      .sort((a, b) => b.b.realInvestment - a.b.realInvestment);
  }, [data, month]);

  useEffect(() => {
    if (selected)
      detailRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "start",
      });
  }, [selected]);

  const withPlan = accounts.filter((a) => a.b.planInvestment > 0);
  const byCompliance = [...withPlan].sort(
    (a, b) => b.compliance - a.compliance
  );
  const byShift = [...withPlan].sort((a, b) => b.shift - a.shift);
  const maxCompliance = Math.max(
    1,
    ...byCompliance.map((a) => a.compliance || 0)
  );
  const maxShift = Math.max(
    0.001,
    ...byShift.map((a) => Math.abs(a.shift || 0))
  );
  const totalPlan = sum(withPlan, (a) => a.b.planInvestment);
  const totalReal = sum(withPlan, (a) => a.b.realInvestment);

  const detail = accounts.find((a) => a.name === selected);

  const rankingRow = (
    index: number,
    name: string,
    bar: React.ReactNode,
    value: React.ReactNode
  ) => (
    <Box
      key={name}
      sx={{
        display: "grid",
        gridTemplateColumns: "22px minmax(100px, 1fr) minmax(90px, 1.4fr) auto",
        gap: 1.25,
        alignItems: "center",
        py: 0.75,
        borderBottom: `1px solid ${theme.palette.grey[100]}`,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 600 }}
      >
        {index}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
        {name}
      </Typography>
      {bar}
      <Box textAlign="right">{value}</Box>
    </Box>
  );

  return (
    <Stack gap={2}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        gap={1}
      >
        <Typography variant="body2" color="text.secondary">
          Cómo reparte cada account la inversión de su cartera frente al
          Business Plan, {period}. Base para el variable.
        </Typography>
        <PeriodSelector
          value={month}
          lastMonth={data.lastMonth}
          onChange={setMonth}
        />
      </Stack>

      <Section
        title="Resumen de la cartera"
        subtitle="Presupuesto conseguido y peso en compra de medios frente al Business Plan. Pulsa una tarjeta para ver los clientes del account."
      >
        {withPlan.length === 0 ? (
          <Note>No hay Business Plan para comparar en el periodo.</Note>
        ) : (
          <Box
            sx={{
              display: "grid",
              gap: 4,
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            }}
          >
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                Presupuesto conseguido frente al plan
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {keur(totalReal)} de {keur(totalPlan)}{" "}
                <Signal
                  text={pct(totalPlan ? totalReal / totalPlan : NaN, 0)}
                  tone={toneOfCompliance(
                    totalPlan ? totalReal / totalPlan : NaN
                  )}
                />
              </Typography>
              {byCompliance.map((a, i) =>
                rankingRow(
                  i + 1,
                  a.name,
                  <ComplianceBar
                    value={a.compliance}
                    max={maxCompliance}
                    width={160}
                    color={
                      toneColor(toneOfCompliance(a.compliance)) ??
                      theme.palette.grey[400]
                    }
                  />,
                  <Typography variant="body2">
                    {pct(a.compliance, 0)}{" "}
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                    >
                      {keur(a.b.realInvestment)} / {keur(a.b.planInvestment)}
                    </Typography>
                  </Typography>
                )
              )}
              <Note>La raya negra marca el 100 % del plan.</Note>
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                Movimiento hacia compra de medios
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {pct(
                  totalPlan
                    ? sum(
                        withPlan,
                        (a) => a.b.planShareCM * a.b.planInvestment
                      ) / totalPlan
                    : NaN,
                  0
                )}{" "}
                →{" "}
                {pct(
                  totalReal
                    ? sum(
                        withPlan,
                        (a) => a.b.realShareCM * a.b.realInvestment
                      ) / totalReal
                    : NaN,
                  0
                )}
              </Typography>
              {byShift.map((a, i) =>
                rankingRow(
                  i + 1,
                  a.name,
                  <Box
                    sx={{
                      position: "relative",
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: theme.palette.grey[200],
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: -3,
                        left: "50%",
                        width: 2,
                        height: 16,
                        backgroundColor: theme.palette.text.primary,
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        ...(a.shift >= 0 ? { left: "50%" } : { right: "50%" }),
                        width: `${(Math.abs(a.shift || 0) / maxShift) * 50}%`,
                        borderRadius: 5,
                        backgroundColor:
                          toneColor(toneOfMarginDiff(a.shift)) ??
                          theme.palette.grey[400],
                      }}
                    />
                  </Box>,
                  <Typography variant="body2">
                    {pp(a.shift, 1)}{" "}
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                    >
                      {pct(a.b.planShareCM, 0)} → {pct(a.b.realShareCM, 0)}
                    </Typography>
                  </Typography>
                )
              )}
              <Note>
                A la derecha de la raya, más peso en compra de medios que en el
                plan; a la izquierda, la inversión se ha ido a Paid Media.
              </Note>
            </Box>
          </Box>
        )}

        <Box sx={{ overflowX: "auto", mt: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Account</TableCell>
                <Num>Presupuesto BP</Num>
                <Num>Presupuesto real</Num>
                <Num>Desvío</Num>
                <Num>Compra de medios BP</Num>
                <Num>Compra de medios real</Num>
                <Num>Hacia compra de medios</Num>
                <Num>Vendido fuera del plan</Num>
                <Num>B. bruto BP</Num>
                <Num>B. bruto real</Num>
                <Num>Cumpl.</Num>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((a) => (
                <TableRow key={a.name} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{a.name}</TableCell>
                  <Num muted>{eur(a.b.planInvestment)}</Num>
                  <Num>{eur(a.b.realInvestment)}</Num>
                  <Num>
                    {a.b.planInvestment ? (
                      <Signal
                        text={pctSigned(a.compliance - 1)}
                        tone={toneOfCompliance(a.compliance)}
                      />
                    ) : (
                      "—"
                    )}
                  </Num>
                  <Num muted>{pct(a.b.planShareCM, 1)}</Num>
                  <Num>{pct(a.b.realShareCM, 1)}</Num>
                  <Num>
                    {Number.isFinite(a.shift) ? (
                      <Signal
                        text={pp(a.shift, 1)}
                        tone={toneOfMarginDiff(a.shift)}
                      />
                    ) : (
                      "—"
                    )}
                  </Num>
                  <Num>{a.outOfPlan > 0 ? eur(a.outOfPlan) : "—"}</Num>
                  <Num muted>{eur(a.b.planProfit)}</Num>
                  <Num>{eur(a.b.realProfit)}</Num>
                  <Num>
                    {a.b.planProfit ? (
                      <Signal
                        text={pct(a.profitCompliance, 0)}
                        tone={toneOfCompliance(a.profitCompliance)}
                      />
                    ) : (
                      "—"
                    )}
                  </Num>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Box mt={3}>
          <TypeLegend />
        </Box>
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            mt: 1,
            gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
          }}
        >
          {accounts.map((a) => (
            <ButtonBase
              key={a.name}
              onClick={() => onSelect(selected === a.name ? "" : a.name)}
              sx={{ textAlign: "left", display: "block", borderRadius: 3 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: `1px solid ${selected === a.name ? theme.palette.text.primary : theme.palette.grey[200]}`,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {a.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {a.clients} clientes · {keur(a.b.realInvestment)} facturados
                  {a.b.planInvestment
                    ? ` · ${pct(a.compliance, 0)} del BP`
                    : ""}
                </Typography>
                <Stack
                  direction="row"
                  gap={1.5}
                  alignItems="center"
                  justifyContent="center"
                  mt={1.5}
                >
                  <Stack alignItems="center">
                    <ShareRing share={a.b.planShareCM} />
                    <Typography variant="caption" color="text.secondary">
                      plan
                    </Typography>
                  </Stack>
                  <Typography color="text.secondary">→</Typography>
                  <Stack alignItems="center">
                    <ShareRing share={a.b.realShareCM} />
                    <Typography variant="caption" color="text.secondary">
                      real
                    </Typography>
                  </Stack>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  textAlign="center"
                >
                  % en compra de medios
                </Typography>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={1}
                  pt={1}
                  sx={{ borderTop: `1px solid ${theme.palette.grey[200]}` }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: toneColor(toneOfMarginDiff(a.shift)),
                    }}
                  >
                    {pp(a.shift, 1)}
                  </Typography>
                  <Signal
                    text={`${eurSigned(a.b.mix)} de beneficio`}
                    tone={toneOfAmount(a.b.mix, a.b.planProfit)}
                  />
                </Stack>
              </Paper>
            </ButtonBase>
          ))}
        </Box>

        <div ref={detailRef} />
        {detail && (
          <Box mt={3}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Clientes de {detail.name}
              </Typography>
              <Link
                component="button"
                onClick={() => onSelect("")}
                underline="hover"
              >
                Cerrar
              </Link>
            </Stack>
            <Note>
              Ordenados de los que más suman por reparto a los que más restan.
            </Note>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Cliente</TableCell>
                    <Num>Facturación BP</Num>
                    <Num>Facturación real</Num>
                    <Num>Cumpl.</Num>
                    <Num>Compra de medios BP</Num>
                    <Num>Compra de medios real</Num>
                    <Num>Diferencia</Num>
                    <Num>Efecto del reparto</Num>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...groupBy(detail.list, (r) => r.clientId).entries()]
                    .map(([id, list]) => {
                      const plan = inPeriod(
                        data.planFor(companyIdsOf(list)),
                        month,
                        data.lastMonth
                      );
                      return { id, b: bridge(list, plan) };
                    })
                    .sort((x, y) => y.b.mix - x.b.mix)
                    .map(({ id, b }) => {
                      const c = b.planInvestment
                        ? b.realInvestment / b.planInvestment
                        : NaN;
                      const d = b.realShareCM - b.planShareCM;
                      return (
                        <TableRow key={id} hover>
                          <TableCell>
                            <Link
                              component="button"
                              underline="hover"
                              onClick={() => onOpenClient(id)}
                            >
                              {data.clientName(id)}
                            </Link>
                          </TableCell>
                          <Num muted>{eur(b.planInvestment)}</Num>
                          <Num>{eur(b.realInvestment)}</Num>
                          <Num>
                            {b.planInvestment ? (
                              <Signal
                                text={pct(c, 0)}
                                tone={toneOfCompliance(c)}
                              />
                            ) : (
                              "—"
                            )}
                          </Num>
                          <Num muted>{pct(b.planShareCM, 0)}</Num>
                          <Num>{pct(b.realShareCM, 0)}</Num>
                          <Num>
                            {Number.isFinite(d) ? (
                              <Signal
                                text={pp(d, 1)}
                                tone={toneOfMarginDiff(d)}
                              />
                            ) : (
                              "—"
                            )}
                          </Num>
                          <Num>
                            <Signal
                              text={eurSigned(b.mix)}
                              tone={toneOfAmount(b.mix, b.planProfit)}
                            />
                          </Num>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}
        <Note>
          El plan de un account es el de las empresas que lleva en el periodo.
          «Vendido fuera del plan» es lo facturado en canales que el Business
          Plan no tenía para esos clientes.
        </Note>
      </Section>
    </Stack>
  );
};

export default AccountsTab;
